
import { useState, useEffect } from 'react';
import { Category } from '@/types';

export const useCategorySelection = (
  categories: Category[],
  onToggle: (categoryId: string) => void,
  expanded: Set<string>
) => {
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);

  // Get selectedCategory from localStorage on initial load
  useEffect(() => {
    const savedCategory = localStorage.getItem('selectedCategory');
    if (savedCategory) {
      try {
        const parsedCategory = JSON.parse(savedCategory);
        const findCategory = (cats: Category[]): Category | null => {
          for (const cat of cats) {
            if (cat.id === parsedCategory.id) {
              return cat;
            }
            const found = findCategory(cat.children);
            if (found) return found;
          }
          return null;
        };
        
        const foundCategory = findCategory(categories);
        if (foundCategory) {
          setSelectedCategory(foundCategory);
        }
      } catch (e) {
        console.error('Error parsing saved category:', e);
      }
    }
  }, []);

  // Update selected category when categories change
  useEffect(() => {
    if (selectedCategory) {
      const findUpdatedCategory = (cats: Category[]): Category | null => {
        for (const cat of cats) {
          if (cat.id === selectedCategory.id) {
            return cat;
          }
          const found = findUpdatedCategory(cat.children);
          if (found) return found;
        }
        return null;
      };
      
      const updatedCategory = findUpdatedCategory(categories);
      if (updatedCategory && JSON.stringify(updatedCategory) !== JSON.stringify(selectedCategory)) {
        console.log('Updating selected category:', updatedCategory);
        setSelectedCategory(updatedCategory);
      }
    }
  }, [categories, selectedCategory]);

  // Save selected category to localStorage
  useEffect(() => {
    if (selectedCategory) {
      localStorage.setItem('selectedCategory', JSON.stringify(selectedCategory));
    } else {
      localStorage.removeItem('selectedCategory');
    }
  }, [selectedCategory]);

  const handleCategorySelect = (category: Category) => {
    console.log('Category selected:', category.name, category.id);
    
    if (selectedCategory?.id === category.id) {
      console.log('Deselecting category:', category.name);
      setSelectedCategory(null);
      return;
    }
    
    setSelectedCategory(category);
    expandParents(category.id);
  };

  const expandParents = (categoryId: string) => {
    const findAndExpandParents = (cats: Category[], parentIds: Set<string> = new Set()): boolean => {
      for (const cat of cats) {
        if (cat.id === categoryId) {
          parentIds.forEach(id => {
            if (!expanded.has(id)) {
              onToggle(id);
            }
          });
          return true;
        }
        
        const newParentIds = new Set(parentIds);
        newParentIds.add(cat.id);
        
        if (findAndExpandParents(cat.children, newParentIds)) {
          return true;
        }
      }
      return false;
    };
    
    findAndExpandParents(categories);
  };

  return {
    selectedCategory,
    setSelectedCategory,
    handleCategorySelect,
  };
};
